import { FilterOperator } from './filters.types'
import type { FilterPropertyConfiguration } from './filter-property-configuration.model'
import { FilterExpectedType, FilterProperty } from './filter-property-configuration.model'

/**
 * Returns whether the given property refers to a custom field rather than a built-in filter property.
 * @param {FilterProperty | string} property The property value to check
 * @returns {boolean} true if the property is a custom field, false if it's a built-in filter property
 */
export function isCustomFieldProperty(property: FilterProperty | string): boolean {
  return !Object.values(FilterProperty).includes(property as FilterProperty)
}

const FLAG_OPERATORS: FilterOperator[] = [FilterOperator.IsSet, FilterOperator.IsNotSet]
const STRING_FILTER_OPERATORS: FilterOperator[] = [FilterOperator.Contains, FilterOperator.DoesNotContain]
const EQUALITY_OPERATORS: FilterOperator[] = [FilterOperator.Equal, FilterOperator.NotEqual]
const BASE_PATH_PROPERTIES: FilterProperty[] = [FilterProperty.TaskCreator, FilterProperty.UserResponsible]
const NAMED_FILTER_PROPERTIES: FilterProperty[] = [
  FilterProperty.Project,
  FilterProperty.User,
  FilterProperty.Company,
  FilterProperty.ProjectType,
  FilterProperty.ProjectStatus,
  FilterProperty.ProjectClient,
  FilterProperty.TypeOfWork,
  FilterProperty.Task
]
const TAG_EXPECTED_TYPES: FilterExpectedType[] = [
  FilterExpectedType.TaskTag,
  FilterExpectedType.ProjectTag,
  FilterExpectedType.UserTag,
  FilterExpectedType.CompanyTag
]
const ENTITY_EXPECTED_TYPES: FilterExpectedType[] = [
  FilterExpectedType.Company,
  FilterExpectedType.Project,
  FilterExpectedType.ProjectType,
  FilterExpectedType.Task,
  FilterExpectedType.Team,
  FilterExpectedType.TypeOfWork,
  FilterExpectedType.User
]

/**
 * Resolves the field for string-based operators (Contains/DoesNotContain).
 * @param {FilterPropertyConfiguration} config
 * @returns {string}
 */
function getFieldForStringOperator(config: FilterPropertyConfiguration): string {
  if (config.isCollection) {
    return `${config.path}/name`
  }

  if (NAMED_FILTER_PROPERTIES.includes(config.property)) {
    return config.path ? `${config.path}/name` : 'name'
  }

  return config.path
}

/**
 * Resolves the field for entity-based expected types with equality operators.
 * @param {FilterPropertyConfiguration} config
 * @returns {string}
 */
function getFieldForEntityType(config: FilterPropertyConfiguration): string {
  if (config.idPath) {
    return config.idPath
  }

  return config.path ? `${config.path}/id` : 'id'
}

/**
 * Returns the field path that a filter condition would use for the given configuration and operator.
 * @param {FilterPropertyConfiguration} config The filter property configuration to resolve the field for
 * @param {FilterOperator} operator The operator used in the filter condition
 * @returns {string} The field path to use for the given configuration and operator
 */
export function getFieldForOperator(config: FilterPropertyConfiguration, operator: FilterOperator): string {
  if (isCustomFieldProperty(config.property)) {
    return config.path
  }

  if (FLAG_OPERATORS.includes(operator)) {
    return config.path
  }

  if (BASE_PATH_PROPERTIES.includes(config.property)) {
    return config.path
  }

  if (STRING_FILTER_OPERATORS.includes(operator)) {
    return getFieldForStringOperator(config)
  }

  const expectedType = config.expectedType[operator]

  if (config.property === FilterProperty.ProjectMembers && EQUALITY_OPERATORS.includes(operator)) {
    // Project member selector values are user IDs, while member entity IDs are internal relation IDs.
    return `${config.path}/userId`
  }

  if (TAG_EXPECTED_TYPES.includes(expectedType)) {
    return `${config.path}/name`
  }

  if (expectedType === FilterExpectedType.Workflow) {
    return config.path
  }

  if (expectedType === FilterExpectedType.ProjectStatus && EQUALITY_OPERATORS.includes(operator)) {
    return config.path ? `${config.path}/name` : 'name'
  }

  if (config.isCollection && expectedType === FilterExpectedType.String && EQUALITY_OPERATORS.includes(operator)) {
    return `${config.path}/name`
  }

  if (ENTITY_EXPECTED_TYPES.includes(expectedType)) {
    return getFieldForEntityType(config)
  }

  return config.path
}

/**
 * Returns the default operator for the given filter property configuration.
 * The default operator is the first key in the expectedType map.
 * @param {FilterPropertyConfiguration} config The filter property configuration
 * @returns {FilterOperator | null} The default operator, or null if no operators are defined
 */
export function getDefaultOperator(config: FilterPropertyConfiguration): FilterOperator | null {
  const operators = Object.keys(config.expectedType) as FilterOperator[]
  return operators.length > 0 ? operators[0] : null
}

/**
 * Finds the filter property configuration that matches the given field path.
 * Resolves by checking which configuration would produce the given field for any of its operators.
 * @param {string} field The condition field path to resolve
 * @param {FilterPropertyConfiguration[]} configurations The available configurations
 * @returns {FilterPropertyConfiguration | undefined} The matching configuration, or undefined if not found
 */
export function findConfigurationForField(
  field: string,
  configurations: FilterPropertyConfiguration[]
): FilterPropertyConfiguration | undefined {
  return configurations.find(config => {
    const operators = Object.keys(config.expectedType) as FilterOperator[]
    return operators.some(operator => getFieldForOperator(config, operator) === field)
  })
}
